import type { CoordinatorLabTourSlug } from './coordinatorLearningLabTours.js';
import type {
  CertificateInnerTab,
  MissionRouteContract,
  MissionRouteSurface,
  MissionStep,
} from './missionTypes.js';

export type MissionLocation = {
  pathname: string;
  search: string;
};

const MODAL_VISIBILITY_ANCHOR: Record<
  NonNullable<MissionRouteContract['requiredModal']>,
  string
> = {
  'create-user': 'coordinator-create-user-form',
  'event-cancel': 'coordinator-event-cancel-modal',
  'agency-edit': 'coordinator-agency-save-btn',
  'outside-training-review': 'coordinator-outside-training-review-modal',
};

const CERTIFICATE_TAB_ANCHOR: Record<
  Exclude<CertificateInnerTab, 'overview'>,
  string
> = {
  templates: 'coordinator-cert-templates',
  issued: 'coordinator-cert-issued-list',
};

export function isDashboardPath(pathname: string): boolean {
  return pathname === '/dashboard' || pathname.endsWith('/dashboard');
}

export function parseMissionLocation(
  loc: MissionLocation | typeof window = typeof window !== 'undefined'
    ? window
    : { pathname: '', search: '' },
): { pathname: string; params: URLSearchParams } {
  if ('pathname' in loc && 'search' in loc && !('location' in loc)) {
    return {
      pathname: loc.pathname,
      params: new URLSearchParams(loc.search),
    };
  }
  const win = loc as Window;
  return {
    pathname: win.location.pathname,
    params: new URLSearchParams(win.location.search),
  };
}

function embedTabFromStep(step: MissionStep): 'settings' | 'code' | 'preview' | undefined {
  const meta = step.metadata?.embedTab;
  if (meta === 'settings' || meta === 'code' || meta === 'preview') {
    return meta;
  }
  const anchor = step.tourAnchor;
  if (anchor?.startsWith('embed-tab-')) {
    const tab = anchor.slice('embed-tab-'.length);
    if (tab === 'settings' || tab === 'code' || tab === 'preview') {
      return tab;
    }
  }
  if (step.id === 'embed_tab' || step.id === 'embed_settings') {
    return 'settings';
  }
  return undefined;
}

function certificateTabFromStep(step: MissionStep): CertificateInnerTab | undefined {
  const meta = step.metadata?.certificateTab;
  if (meta === 'overview' || meta === 'templates' || meta === 'issued') {
    return meta;
  }
  if (step.tourAnchor === 'coordinator-cert-templates' || step.id === 'cert_templates') {
    return 'templates';
  }
  if (
    step.tourAnchor === 'coordinator-cert-issued-list' ||
    step.tourAnchor === 'coordinator-cert-tab-issued' ||
    step.id === 'cert_issued_tab' ||
    step.id === 'cert_issued'
  ) {
    return 'issued';
  }
  return undefined;
}

function requiredModalFromStep(step: MissionStep): MissionRouteContract['requiredModal'] {
  const meta = step.metadata?.requiredModal;
  if (
    meta === 'create-user' ||
    meta === 'event-cancel' ||
    meta === 'agency-edit' ||
    meta === 'outside-training-review'
  ) {
    return meta;
  }
  if (step.id === 'user_modal' || step.id === 'user_submit') {
    return 'create-user';
  }
  if (
    step.id === 'cancel_modal' ||
    step.id === 'cancel_reason' ||
    step.id === 'cancel_confirm'
  ) {
    return 'event-cancel';
  }
  if (step.id === 'profile_save') {
    return 'agency-edit';
  }
  if (step.id === 'outside_modal' || step.id === 'outside_submit') {
    return 'outside-training-review';
  }
  return undefined;
}

function inferSurface(step: MissionStep): MissionRouteSurface {
  if (step.placement === 'center' && (step.kind === 'intro' || step.kind === 'complete')) {
    const hasDashboardAnchor =
      step.coordinatorTabSlug ||
      step.tourAnchor?.startsWith('coordinator-tab-') ||
      step.tourAnchor?.startsWith('coordinator-venue') ||
      step.tourAnchor?.startsWith('coordinator-cert') ||
      step.tourAnchor?.startsWith('coordinator-outside-training') ||
      step.tourAnchor?.startsWith('embed-tab-') ||
      step.tourAnchor?.startsWith('coordinator-agency') ||
      step.tourAnchor?.startsWith('coordinator-event-row') ||
      step.tourAnchor === 'coordinator-create-event-btn' ||
      step.tourAnchor === 'coordinator-create-user-btn';
    if (!hasDashboardAnchor && !step.tourAnchor?.startsWith('event-form-')) {
      return 'any';
    }
  }

  const anchor = step.tourAnchor ?? '';
  if (
    anchor.startsWith('event-form-') ||
    step.advanceGate.type === 'route' ||
    step.metadata?.enableDraftRedirect === true
  ) {
    return 'eventForm';
  }

  if (
    step.coordinatorTabSlug ||
    anchor.startsWith('coordinator-') ||
    anchor.startsWith('embed-tab-')
  ) {
    return 'dashboard';
  }

  return 'any';
}

/** Resolve required route state for a playbook step (Appendix E). */
export function resolveMissionRouteContract(
  step: MissionStep,
  _slug?: CoordinatorLabTourSlug,
): MissionRouteContract {
  const surface = inferSurface(step);
  const contract: MissionRouteContract = { surface };

  if (surface === 'dashboard') {
    if (step.coordinatorTabSlug) {
      contract.coordinatorTab = step.coordinatorTabSlug;
    } else if (step.tourAnchor?.startsWith('coordinator-tab-')) {
      contract.coordinatorTab = step.tourAnchor.slice('coordinator-tab-'.length);
    } else if (step.tourAnchor?.startsWith('embed-tab-')) {
      contract.coordinatorTab = 'embed';
    }

    const embedTab = embedTabFromStep(step);
    if (embedTab) {
      contract.embedTab = embedTab;
      contract.coordinatorTab = contract.coordinatorTab ?? 'embed';
    }

    const certTab = certificateTabFromStep(step);
    if (certTab && certTab !== 'overview') {
      contract.certificateTab = certTab;
      contract.coordinatorTab = contract.coordinatorTab ?? 'certificates';
    }
  }

  if (surface === 'eventForm') {
    const stepperIndex = step.metadata?.stepperIndex;
    if (typeof stepperIndex === 'number') {
      contract.eventFormStepperIndex = stepperIndex;
    }
  }

  const modal = requiredModalFromStep(step);
  if (modal) {
    contract.requiredModal = modal;
    if (!contract.coordinatorTab) {
      if (modal === 'create-user') {
        contract.coordinatorTab = 'members';
      } else if (modal === 'event-cancel') {
        contract.coordinatorTab = 'events';
      } else if (modal === 'agency-edit') {
        contract.coordinatorTab = 'settings';
      } else if (modal === 'outside-training-review') {
        contract.coordinatorTab = 'outside-training';
      }
      contract.surface = 'dashboard';
    }
  }

  const rowAnchor = step.metadata?.rowAnchor;
  if (typeof rowAnchor === 'string' && rowAnchor.length > 0) {
    contract.rowAnchor = rowAnchor;
  } else if (step.id === 'cancel_button') {
    contract.rowAnchor = 'coordinator-event-row-event_leadership_cancel';
  } else if (step.id === 'outside_review_btn') {
    contract.rowAnchor = 'coordinator-outside-training-row-pending';
  }

  return contract;
}

export function buildMissionNavigatePath(contract: MissionRouteContract): string | null {
  if (contract.surface === 'any') {
    return null;
  }

  if (contract.surface === 'eventForm') {
    return '/coordinator-event-create?learningLab=1';
  }

  const params = new URLSearchParams();
  params.set('role', 'coordinator');
  params.set('learningLab', '1');
  if (contract.coordinatorTab) {
    params.set('tab', contract.coordinatorTab);
  }
  if (contract.embedTab) {
    params.set('embedTab', contract.embedTab);
  }
  if (contract.certificateTab && contract.certificateTab !== 'overview') {
    params.set('certTab', contract.certificateTab);
  }
  return `/dashboard?${params.toString()}`;
}

function isElementVisible(selector: string, root: ParentNode): boolean {
  if (typeof document === 'undefined') {
    return true;
  }
  const el = root.querySelector(selector);
  if (!(el instanceof HTMLElement)) {
    return false;
  }
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function isModalSatisfied(
  modal: NonNullable<MissionRouteContract['requiredModal']>,
  root: ParentNode,
): boolean {
  const anchor = MODAL_VISIBILITY_ANCHOR[modal];
  return isElementVisible(`[data-tour="${anchor}"], [data-tour-stable="${anchor}"]`, root);
}

function isCertificateTabSatisfied(
  tab: CertificateInnerTab,
  root: ParentNode,
): boolean {
  if (tab === 'overview') {
    return true;
  }
  const anchor = CERTIFICATE_TAB_ANCHOR[tab];
  return isElementVisible(`[data-tour="${anchor}"], [data-tour-stable="${anchor}"]`, root);
}

export function isMissionRouteSatisfied(
  contract: MissionRouteContract,
  loc?: MissionLocation,
  root: ParentNode = typeof document !== 'undefined' ? document : ({} as ParentNode),
): boolean {
  if (contract.surface === 'any') {
    return true;
  }

  const { pathname, params } = parseMissionLocation(loc);

  if (contract.surface === 'eventForm') {
    if (!pathname.includes('/coordinator-event')) {
      return false;
    }
    if (params.get('learningLab') !== '1') {
      return false;
    }
    return true;
  }

  if (!isDashboardPath(pathname)) {
    return false;
  }
  if (params.get('role') !== 'coordinator') {
    return false;
  }
  if (params.get('learningLab') !== '1') {
    return false;
  }
  if (contract.coordinatorTab && params.get('tab') !== contract.coordinatorTab) {
    return false;
  }
  if (contract.embedTab && params.get('embedTab') !== contract.embedTab) {
    return false;
  }
  if (
    contract.certificateTab &&
    contract.certificateTab !== 'overview' &&
    !isCertificateTabSatisfied(contract.certificateTab, root)
  ) {
    return false;
  }

  if (contract.requiredModal && !isModalSatisfied(contract.requiredModal, root)) {
    return false;
  }

  return true;
}

export function missionStepRouteContractFromTourMetadata(
  metadata: Record<string, unknown> | undefined,
  slug: CoordinatorLabTourSlug,
): MissionRouteContract | null {
  if (!metadata?.missionStepId) {
    return null;
  }
  const step: MissionStep = {
    id: String(metadata.missionStepId),
    stepName: String(metadata.missionStepId),
    kind: (metadata.missionKind as MissionStep['kind']) ?? 'spotlight',
    groupId: String(metadata.missionGroupId ?? 'start'),
    title: '',
    instruction: '',
    advanceGate: metadata.advanceGate as MissionStep['advanceGate'],
    tourAnchor:
      typeof metadata.tourAnchor === 'string' ? metadata.tourAnchor : undefined,
    coordinatorTabSlug:
      typeof metadata.coordinatorTabSlug === 'string'
        ? metadata.coordinatorTabSlug
        : undefined,
    module: metadata.module as MissionStep['module'],
    placement: metadata.placement as MissionStep['placement'],
    metadata,
    isPanelTarget: metadata.isPanelTarget === true,
  };
  return resolveMissionRouteContract(step, slug);
}
